import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

const TYPES = [
    { v: "event_sponsorship", t: "Event Sponsorship" },
    { v: "product_placement", t: "Product Placement" },
    { v: "welcome_bag", t: "Welcome Bag Inclusion" },
    { v: "series_integration", t: "Original Series Integration" },
    { v: "social_content", t: "Social Media Content" },
    { v: "bts_crew", t: "BTS Crew (gear provided)" },
    { v: "custom_activation", t: "Custom Activation" },
];

// Query-string presets used by the /original-series partner-apply cards.
// Anything not listed here just prefills partnership_type + drops the raw
// `interest` string into the message body.
const KNOWN_TYPES = new Set(TYPES.map((t) => t.v));

export default function Partnerships() {
    const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", partnership_type: "event_sponsorship", budget_range: "", message: "" });
    const [busy, setBusy] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    // Prefill from ?type=…&interest=… so partner cards on other pages can
    // route the applicant straight into the right bucket with context intact.
    useEffect(() => {
        const type = (searchParams.get("type") || "").trim();
        const interest = (searchParams.get("interest") || "").trim();
        if (!type && !interest) return;
        setForm((s) => ({
            ...s,
            partnership_type: KNOWN_TYPES.has(type) ? type : s.partnership_type,
            message: interest
                ? `Interested in: ${interest}\n\n${s.message}`.trim()
                : s.message,
        }));
        // Show the applicant we understood their intent — small trust signal.
        if (interest) toast.success(`Prefilled for: ${interest}`);
        // Clean the URL so a refresh doesn't stack "Interested in:" lines.
        const params = new URLSearchParams(searchParams);
        params.delete("type");
        params.delete("interest");
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/partnerships/inquiries", form);
            toast.success("Thank you. Our partnerships team will be in touch.");
            setForm({ company: "", contact_name: "", email: "", phone: "", partnership_type: "event_sponsorship", budget_range: "", message: "" });
        } catch (err) { toast.error("Something went wrong. Try again."); }
        finally { setBusy(false); }
    };

    return (
        <main data-testid="partnerships-page" className="pt-32 pb-32">
            <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
                <p className="eyebrow">Partnerships</p>
                <h1 className="font-display text-6xl md:text-8xl mt-4 max-w-4xl">Build with us. <em className="text-[color:var(--gold)]">Beautifully</em>.</h1>
                <p className="mt-8 max-w-2xl text-[color:var(--cream-muted)] text-lg">Beverage houses. Local kitchens. Community organizations. Legacy brands. Aspiring crew and creators. We collaborate with partners who understand that a room is a message.</p>
            </section>

            <section className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-24 grid lg:grid-cols-12 gap-16">
                <div className="lg:col-span-7">
                    <p className="eyebrow">Partnership tracks</p>
                    <div className="mt-10 grid md:grid-cols-2 gap-8">
                        {TYPES.map((t, i) => (
                            <button
                                type="button"
                                key={t.v}
                                onClick={() => {
                                    setForm((s) => ({ ...s, partnership_type: t.v }));
                                    document.getElementById("partner-inquiry-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
                                }}
                                className={`text-left border p-8 transition-colors duration-300 ${form.partnership_type === t.v ? "border-[color:var(--gold)] bg-[color:var(--gold)]/5" : "border-[color:var(--espresso)] hover:border-[color:var(--gold)]"}`}
                                data-testid={`partnership-track-${i}`}
                            >
                                <span className="font-serif italic text-[color:var(--gold)]">0{i + 1}</span>
                                <h3 className="font-serif text-2xl mt-2">{t.t}</h3>
                                <p className="mt-3 text-sm text-[color:var(--cream-muted)]">Bespoke placement designed to align with our editorial voice and community values.</p>
                            </button>
                        ))}
                    </div>
                </div>

                <aside id="brands" className="lg:col-span-5 border border-[color:var(--espresso)] p-10 lg:sticky lg:top-32 self-start">
                    <p className="eyebrow">Start a conversation</p>
                    <h3 className="font-serif text-3xl mt-3">Tell us about your brand.</h3>
                    <form id="partner-inquiry-form" className="mt-8 space-y-6" onSubmit={submit}>
                        <input className="input-line" required placeholder="Company / brand" value={form.company} onChange={set("company")} data-testid="partner-company" />
                        <input className="input-line" required placeholder="Contact name" value={form.contact_name} onChange={set("contact_name")} data-testid="partner-name" />
                        <input className="input-line" required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="partner-email" />
                        <input className="input-line" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="partner-phone" />
                        <div>
                            <label className="text-xs uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Partnership type</label>
                            <select className="input-line" value={form.partnership_type} onChange={set("partnership_type")} data-testid="partner-type">
                                {TYPES.map((t) => <option key={t.v} value={t.v} className="bg-[color:var(--warm-black)]">{t.t}</option>)}
                            </select>
                        </div>
                        <input className="input-line" placeholder="Budget range (optional)" value={form.budget_range} onChange={set("budget_range")} data-testid="partner-budget" />
                        <textarea className="input-line" required placeholder="Tell us about your brand and what you're imagining." value={form.message} onChange={set("message")} data-testid="partner-message" />
                        <button type="submit" disabled={busy} className="btn-gold btn-gold-solid w-full justify-center" data-testid="partner-submit">{busy ? "Sending…" : "Send inquiry"}</button>
                    </form>
                </aside>
            </section>
        </main>
    );
}
